import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AuthGuard } from '@/components/AuthGuard';
import { Toaster } from 'sonner';
import { LayoutDashboard, ShoppingBag, Plus, LogOut, Settings, ChevronRight, User, Moon, Sun, Menu, X, Shirt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

export function AdminLayout() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'light') {
      setIsDarkMode(false);
      document.documentElement.classList.remove('dark-mode');
      document.documentElement.classList.add('light-mode');
    } else {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark-mode');
      document.documentElement.classList.remove('light-mode');
    }
  }, []);

  // Close sidebar when navigating on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    if (!isDarkMode) {
      document.documentElement.classList.add('dark-mode');
      document.documentElement.classList.remove('light-mode');
      localStorage.setItem('theme', 'dark');
      toast.success('Dark mode enabled');
    } else {
      document.documentElement.classList.remove('dark-mode');
      document.documentElement.classList.add('light-mode');
      localStorage.setItem('theme', 'light');
      toast.success('Light mode enabled');
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <AuthGuard>
      <div className={`min-h-screen bg-background flex flex-col ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
        <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-16 items-center gap-2 px-4 border-b">
            <button onClick={toggleSidebar} className="-ml-2 rounded-md p-1.5 text-muted-foreground lg:hidden">
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/admin" className="flex items-center gap-2 font-semibold">
              <span className="font-semibold text-xl">Dexfinds Panel</span>
            </Link>
            <div className="flex items-center ml-auto gap-2">
              <div className="flex items-center gap-2 mr-2">
                <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="relative">
                  {isDarkMode ? <Moon size={18} /> : <Sun size={18} />}
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden">
          <aside 
            className={`fixed inset-y-0 left-0 z-30 mt-14 overflow-y-auto bg-background border-r transition-all duration-300 
              ${isMobile ? (sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full') : 'translate-x-0 w-64 md:w-52 lg:w-64'}`}
          >
            <div className="py-4">
              <div className="px-4 py-2">
                <div className="flex items-center gap-2 py-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <User size={18} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium truncate">{user?.email}</span>
                    <span className="text-xs text-muted-foreground">Admin</span>
                  </div>
                </div>
              </div>
              <nav className="mt-5 px-2 space-y-1">
                <Link
                  to="/admin"
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive('/admin')
                      ? 'bg-primary text-white'
                      : 'text-foreground hover:bg-primary/10'
                  }`}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <LayoutDashboard className="mr-3 h-5 w-5" />
                  <span className="whitespace-nowrap">Dashboard</span>
                  <ChevronRight className={`ml-auto h-5 w-5 ${isActive('/admin') ? 'opacity-100' : 'opacity-0'} transition-opacity`} />
                </Link>

                <Link
                  to="/admin/clothinglist"
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive('/admin/clothinglist')
                      ? 'bg-primary text-white'
                      : 'text-foreground hover:bg-primary/10'
                  }`}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <ShoppingBag className="mr-3 h-5 w-5" />
                  <span className="whitespace-nowrap">Products</span>
                  <ChevronRight className={`ml-auto h-5 w-5 ${isActive('/admin/clothinglist') ? 'opacity-100' : 'opacity-0'} transition-opacity`} />
                </Link>

                <Link
                  to="/admin/clothinglist/new"
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive('/admin/clothinglist/new')
                      ? 'bg-primary text-white'
                      : 'text-foreground hover:bg-primary/10'
                  }`}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <Plus className="mr-3 h-5 w-5" />
                  <span className="whitespace-nowrap">Add Product</span>
                  <ChevronRight className={`ml-auto h-5 w-5 ${isActive('/admin/clothinglist/new') ? 'opacity-100' : 'opacity-0'} transition-opacity`} />
                </Link>
                
                <Link
                  to="/admin/settings"
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive('/admin/settings')
                      ? 'bg-primary text-white'
                      : 'text-foreground hover:bg-primary/10'
                  }`}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  <Settings className="mr-3 h-5 w-5" />
                  <span className="whitespace-nowrap">Settings</span>
                  <ChevronRight className={`ml-auto h-5 w-5 ${isActive('/admin/settings') ? 'opacity-100' : 'opacity-0'} transition-opacity`} />
                </Link>
                
                <button
                  onClick={() => signOut()}
                  className="w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md text-foreground hover:bg-destructive/10 transition-colors"
                >
                  <LogOut className="mr-3 h-5 w-5" />
                  <span className="whitespace-nowrap">Sign Out</span>
                </button>
              </nav>
            </div>
          </aside>

          <main className={`flex-1 overflow-y-auto pt-14 transition-all duration-300 ${isMobile ? '' : 'md:ml-52 lg:ml-64'}`}>
            <div className="container mx-auto p-6">
              <Outlet />
            </div>
          </main>
        </div>
        <Toaster />
      </div>
    </AuthGuard>
  );
}
